'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const PreferencesSection = () => {
  const [preferences, setPreferences] = useState({
    preferredAirlines: ['British Airways', 'Emirates', 'Lufthansa'],
    cabinClass: 'economy',
    budgetMin: 500,
    budgetMax: 5000,
    travelPurpose: ['leisure', 'business'],
    notifications: {
      email: true,
      sms: false,
      push: true,
    },
    currency: 'GBP',
    language: 'English',
  });

  const cabinClasses = ['economy', 'premium-economy', 'business', 'first'];
  const travelPurposes = ['leisure', 'business', 'family', 'adventure'];

  return (
    <div className="space-y-6">
      {/* Travel Preferences */}
      <div className="p-6 bg-white rounded-xl border border-border">
        <h3 className="text-xl font-black text-foreground mb-4 flex items-center gap-2">
          <Icon name="AdjustmentsHorizontalIcon" size={24} className="text-primary" />
          Travel Preferences
        </h3>

        {/* Preferred Airlines */}
        <div className="mb-6">
          <label className="block text-sm font-bold text-foreground mb-2">
            Preferred Airlines
          </label>
          <input
            type="text"
            value={preferences.preferredAirlines.join(', ')}
            onChange={(e) => {
              const airlines = e.target.value.split(',').map(a => a.trim()).filter(Boolean);
              setPreferences({ ...preferences, preferredAirlines: airlines });
            }}
            className="w-full px-4 py-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            placeholder="e.g., British Airways, Emirates"
          />
        </div>

        {/* Cabin Class */}
        <div className="mb-6">
          <label className="block text-sm font-bold text-foreground mb-2">
            Preferred Cabin Class
          </label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {cabinClasses.map((cabin) => (
              <button
                key={cabin}
                onClick={() => setPreferences({ ...preferences, cabinClass: cabin })}
                className={`px-4 py-3 rounded-lg font-bold text-sm transition-all duration-200 ${
                  preferences.cabinClass === cabin
                    ? 'bg-primary text-white shadow-md'
                    : 'bg-surface text-foreground hover:bg-primary/10 border border-border'
                }`}
              >
                {cabin.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Range */}
        <div className="mb-6">
          <label className="block text-sm font-bold text-foreground mb-2">
            Budget Range (GBP)
          </label>
          <div className="flex gap-4">
            <div className="flex-1">
              <input
                type="number"
                value={preferences.budgetMin}
                onChange={(e) => setPreferences({ ...preferences, budgetMin: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Min"
              />
            </div>
            <div className="flex-1">
              <input
                type="number"
                value={preferences.budgetMax}
                onChange={(e) => setPreferences({ ...preferences, budgetMax: parseInt(e.target.value) || 10000 })}
                className="w-full px-4 py-3 border-2 border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                placeholder="Max"
              />
            </div>
          </div>
        </div>

        {/* Travel Purpose */}
        <div>
          <label className="block text-sm font-bold text-foreground mb-2">
            Travel Purposes
          </label>
          <div className="flex flex-wrap gap-2">
            {travelPurposes.map((purpose) => (
              <button
                key={purpose}
                onClick={() => {
                  const updated = preferences.travelPurpose.includes(purpose)
                    ? preferences.travelPurpose.filter(p => p !== purpose)
                    : [...preferences.travelPurpose, purpose];
                  setPreferences({ ...preferences, travelPurpose: updated });
                }}
                className={`px-4 py-2 rounded-full font-semibold text-sm transition-all duration-200 ${
                  preferences.travelPurpose.includes(purpose)
                    ? 'bg-accent text-white shadow-md'
                    : 'bg-surface text-foreground hover:bg-accent/10 border border-border'
                }`}
              >
                {purpose.charAt(0).toUpperCase() + purpose.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="p-6 bg-white rounded-xl border border-border">
        <h3 className="text-xl font-black text-foreground mb-4 flex items-center gap-2">
          <Icon name="BellIcon" size={24} className="text-accent" />
          Notification Preferences
        </h3>
        <div className="space-y-4">
          {[
            { key: 'email', label: 'Email Notifications', description: 'Receive booking confirmations and updates via email' },
            { key: 'sms', label: 'SMS Notifications', description: 'Get flight alerts and reminders via text message' },
            { key: 'push', label: 'Push Notifications', description: 'Real-time updates on your device' },
          ].map((item) => (
            <div key={item.key} className="flex items-center justify-between p-4 bg-surface rounded-lg">
              <div className="flex-1">
                <p className="font-bold text-foreground">{item.label}</p>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
              <button
                onClick={() => {
                  setPreferences({
                    ...preferences,
                    notifications: {
                      ...preferences.notifications,
                      [item.key]: !preferences.notifications[item.key as keyof typeof preferences.notifications],
                    },
                  });
                }}
                className={`w-14 h-8 rounded-full transition-all duration-200 ${
                  preferences.notifications[item.key as keyof typeof preferences.notifications]
                    ? 'bg-trust-green' :'bg-gray-300'
                }`}
              >
                <div
                  className={`w-6 h-6 bg-white rounded-full shadow-md transition-transform duration-200 ${
                    preferences.notifications[item.key as keyof typeof preferences.notifications]
                      ? 'translate-x-7' :'translate-x-1'
                  }`}
                />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button className="px-8 py-3 gradient-gold-shiny text-primary font-black rounded-lg shadow-md hover:shadow-lg transition-all duration-200">
          Save All Preferences
        </button>
      </div>
    </div>
  );
};

export default PreferencesSection;